export const BUSINESS_CASE_STORAGE_KEY = "rcuk-business-case-builder-v2";

export const SUCCESS_FEE_PER_HIRE_GBP = 350;

export const SUCCESS_FEE_TRIGGER = {
  OFFER_ACCEPTED: "Offer accepted",
  EMPLOYMENT_COMMENCED: "Employment commenced",
  OTHER_CONTRACTUAL_TRIGGER: "Other agreed contractual trigger",
};

export const DEFAULT_BENEFITS_TEXT =
  "Improved candidate readiness, reduced recruiter rework, faster manager decision cycles, and clearer evidence quality before formal assessment booking.";

export const DEFAULT_RISKS_TEXT =
  "Candidate information can become out of date, candidate availability may change, and first-pilot conversion assumptions may be optimistic. Controls: reconfirm candidate interest before introduction, revalidate key profile details, check duplicates in the employer ATS, retain employer-led checks and review KPIs weekly before any scale-up decision.";

export const DEFAULT_PILOT_TEXT =
  "Run a controlled pilot over one recruitment intake with agreed role scope, weekly KPI review, and post-campaign variance analysis before scale-up.";

export const DEFAULT_DECISION_REQUEST =
  "Approve a controlled pilot of the Railway Careers UK Talent Pool for this recruitment requirement and authorise stage-by-stage measurement against the baseline model.";

export const PILOT_HYPOTHESIS =
  "The pilot will test whether Railway Careers UK can provide sufficient geographically and occupationally relevant candidates while reducing recruiter review time, operational shortlisting time and interview-panel demand without reducing formal assessment performance, equality outcomes, appointment quality or retention.";

export const benchmarkDefaults = {
  companyName: "",
  campaignName: "Conductor",
  roleBeingRecruited: "Conductor",
  depotLocation: "",
  preparedBy: "",
  targetDecisionDate: "",
  currentRecruitmentPressure: "",
  decisionRequested: DEFAULT_DECISION_REQUEST,

  appointmentsRequired: 8,
  successfulRailwayHires: 4,
  campaignsPerYear: 4,

  conventionalApplicationsReceived: 1000,
  conventionalGatewayPass: 350,
  conventionalOperationalShortlist: 60,
  conventionalInterviewed: 30,

  talentProfilesReviewed: 10,
  talentOperationalShortlist: 8,
  talentInterviewed: 6,

  gatewaySetupHours: 4,
  conventionalReviewMinutes: 5,
  fixedSchedulingHours: 4,
  schedulingMinutesPerInterview: 15,
  offerProgressionHours: 1,

  talentRouteSetupHours: 1,
  talentReviewMinutes: 6,
  talentFixedSchedulingHours: 2,

  conventionalOpsShortlistHours: 7.5,
  talentOpsShortlistHours: 3.75,
  operationalPanelMembers: 1,
  hrPanelMembers: 1,
  candidatesPerInterviewDay: 8,
  interviewDayHours: 7.5,

  hrAnnualSalary: 38000,
  operationalAnnualSalary: 60000,
  weeklyContractedHours: 37.5,
  productiveWeeksPerYear: 46.4,
  employerNiRatePct: 15,
  employerNiThreshold: 5000,
  employerPensionRatePct: 3,
  additionalOverheadPct: 0,

  talentPoolAccessCostPerCampaign: 0,
  feePerSuccessfulHire: SUCCESS_FEE_PER_HIRE_GBP,
  successFeePaymentTrigger: SUCCESS_FEE_TRIGGER.EMPLOYMENT_COMMENCED,

  // Legacy fields preserved for backward compatibility with saved local data.
  vacanciesToFill: 8,
  successfulOtherRouteHires: 4,
  conventionalApplicationsAutoRejected: 650,
  conventionalApplicationsManualReview: 350,
  conventionalInvitedToInterview: 40,
  conventionalInterviewAttending: 30,
  conventionalSuccessfulHires: 8,
  talentCandidatesInvited: 8,
  talentInvitationAccepted: 7,
  conventionalCampaignSetupHours: 4,
  conventionalShortlistAdminHours: 4,
  conventionalRejectionAdminHours: 4,
  conventionalOfferProgressionHours: 1,
  talentSearchSetupHours: 1,
  talentInvitationAdminMinutes: 4,
  talentSchedulingMinutesPerInterview: 12,
  talentOfferProgressionHours: 1,
  otherPanelMembers: 0,
  useCompleteBookedDaysForInterviewCosts: false,
  otherPanelAnnualSalary: 45000,
  vacancyAdvertisingCost: 0,
  recruitmentSupplierCost: 0,
  venueCostPerDay: 0,
  operationalBackfillCostPerManagerDay: 0,
  paidAssessmentCostPerCandidate: 0,
  medicalCostPerCandidate: 0,
  otherConventionalCampaignCosts: 0,
  otherTalentCampaignCosts: 0,
  conventionalPaidAssessmentCandidates: 0,
  talentPaidAssessmentCandidates: 0,
  conventionalMedicalCandidates: 0,
  talentMedicalCandidates: 0,

  conservativeAcceptancePct: 65,
  conservativeInterviewAttendancePct: 80,
  conservativeInterviewToHirePct: 45,
  conservativeConventionalInterviewVolumePct: 115,
  conservativeOperationalShortlistTimePct: 120,
  conservativePaidAssessmentReductionPct: 10,

  expectedAcceptancePct: 80,
  expectedInterviewAttendancePct: 90,
  expectedInterviewToHirePct: 60,
  expectedConventionalInterviewVolumePct: 100,
  expectedOperationalShortlistTimePct: 100,
  expectedPaidAssessmentReductionPct: 20,

  optimisticAcceptancePct: 95,
  optimisticInterviewAttendancePct: 95,
  optimisticInterviewToHirePct: 80,
  optimisticConventionalInterviewVolumePct: 85,
  optimisticOperationalShortlistTimePct: 85,
  optimisticPaidAssessmentReductionPct: 40,

  additionalExpectedBenefits: DEFAULT_BENEFITS_TEXT,
  risksAndControls: DEFAULT_RISKS_TEXT,
  recommendedPilotScope: DEFAULT_PILOT_TEXT,
};

export const scenarioModeKeys = ["conservative", "expected", "optimistic"];

export const assumptionLabels = {
  companyName: "Company or business unit",
  campaignName: "Recruitment campaign or role",
  roleBeingRecruited: "Role being recruited",
  depotLocation: "Depot or location",
  preparedBy: "Prepared by",
  targetDecisionDate: "Target decision date",
  currentRecruitmentPressure: "Current recruitment pressure",
  decisionRequested: "Decision requested",
  appointmentsRequired: "Appointments required",
  vacanciesToFill: "Vacancies to be filled",
  successfulRailwayHires: "Successful Railway Careers UK hires",
  successfulOtherRouteHires: "Successful hires from other routes",
  campaignsPerYear: "Comparable campaigns per year",
  conventionalApplicationsReceived: "Conventional applications received",
  conventionalGatewayPass: "Applications passing automatic gateway",
  conventionalApplicationsAutoRejected: "Conventional applications rejected automatically",
  conventionalApplicationsManualReview: "Conventional applications manually reviewed",
  conventionalOperationalShortlist: "Conventional operational shortlist",
  conventionalInterviewed: "Conventional candidates interviewed",
  conventionalInvitedToInterview: "Conventional candidates invited to interview",
  conventionalInterviewAttending: "Conventional candidates attending interview",
  conventionalSuccessfulHires: "Conventional successful hires",
  talentProfilesReviewed: "Talent Pool profiles reviewed",
  talentOperationalShortlist: "Talent Pool profiles sent to operational shortlisting",
  talentCandidatesInvited: "Talent Pool candidates invited",
  talentInvitationAccepted: "Talent Pool candidates accepting invitation",
  talentInterviewed: "Talent Pool candidates interviewed",
  gatewaySetupHours: "Automatic gateway setup and exception-handling hours",
  conventionalReviewMinutes: "Manual application review time per application (minutes)",
  fixedSchedulingHours: "Conventional fixed interview scheduling hours",
  schedulingMinutesPerInterview: "Conventional interview scheduling time per candidate (minutes)",
  offerProgressionHours: "Offer administration time per appointee (hours)",
  conventionalRejectionAdminHours: "Conventional rejection administration hours",
  conventionalOfferProgressionHours: "Conventional offer administration time per successful hire (hours)",
  talentRouteSetupHours: "Talent Pool route setup hours",
  talentSearchSetupHours: "Talent Pool search and setup hours",
  talentReviewMinutes: "Talent Pool profile review time per candidate (minutes)",
  talentFixedSchedulingHours: "Talent Pool fixed scheduling hours",
  talentInvitationAdminMinutes: "Talent Pool invitation administration time per candidate (minutes)",
  talentSchedulingMinutesPerInterview: "Talent Pool scheduling time per interviewee (minutes)",
  talentOfferProgressionHours: "Talent Pool offer administration time per successful hire (hours)",
  conventionalOpsShortlistHours: "Conventional operational shortlisting hours",
  talentOpsShortlistHours: "Talent Pool operational shortlisting hours",
  operationalPanelMembers: "Operational managers on interview panel",
  hrPanelMembers: "HR or recruitment representatives on panel",
  otherPanelMembers: "Other panel members",
  candidatesPerInterviewDay: "Candidates interviewed per day",
  interviewDayHours: "Length of interview day (hours)",
  useCompleteBookedDaysForInterviewCosts: "Cost interview activity using complete booked days",
  hrAnnualSalary: "Recruitment or HR annual salary",
  operationalAnnualSalary: "Operational manager annual salary",
  otherPanelAnnualSalary: "Other panel member annual salary",
  weeklyContractedHours: "Weekly contracted hours",
  productiveWeeksPerYear: "Productive weeks per year",
  employerNiRatePct: "Employer National Insurance rate (%)",
  employerNiThreshold: "Employer National Insurance threshold",
  employerPensionRatePct: "Employer pension contribution (%)",
  additionalOverheadPct: "Additional overhead (%)",
  vacancyAdvertisingCost: "Vacancy advertising cost",
  recruitmentSupplierCost: "Recruitment supplier cost",
  talentPoolAccessCostPerCampaign: "Railway Careers UK access or implementation cost per campaign",
  venueCostPerDay: "Interview venue cost per day",
  operationalBackfillCostPerManagerDay: "Operational backfill cost per manager-day",
  paidAssessmentCostPerCandidate: "Paid assessment cost per candidate",
  medicalCostPerCandidate: "Medical cost per candidate",
  otherConventionalCampaignCosts: "Other conventional campaign cost",
  otherTalentCampaignCosts: "Other Talent Pool route cost",
  conventionalPaidAssessmentCandidates: "Conventional candidates receiving paid assessment",
  talentPaidAssessmentCandidates: "Talent Pool candidates receiving paid assessment",
  conventionalMedicalCandidates: "Conventional candidates receiving paid medical",
  talentMedicalCandidates: "Talent Pool candidates receiving paid medical",
  feePerSuccessfulHire: "Fee per successful hire",
  successFeePaymentTrigger: "Success-fee payment trigger",
  conservativeAcceptancePct: "Conservative invitation acceptance (%)",
  conservativeInterviewAttendancePct: "Conservative interview attendance (%)",
  conservativeInterviewToHirePct: "Conservative interview-to-hire conversion (%)",
  conservativeConventionalInterviewVolumePct: "Conservative conventional interview volume (%)",
  conservativeOperationalShortlistTimePct: "Conservative operational shortlist time (%)",
  conservativePaidAssessmentReductionPct: "Conservative paid-assessment reduction (%)",
  expectedAcceptancePct: "Expected invitation acceptance (%)",
  expectedInterviewAttendancePct: "Expected interview attendance (%)",
  expectedInterviewToHirePct: "Expected interview-to-hire conversion (%)",
  expectedConventionalInterviewVolumePct: "Expected conventional interview volume (%)",
  expectedOperationalShortlistTimePct: "Expected operational shortlist time (%)",
  expectedPaidAssessmentReductionPct: "Expected paid-assessment reduction (%)",
  optimisticAcceptancePct: "Optimistic invitation acceptance (%)",
  optimisticInterviewAttendancePct: "Optimistic interview attendance (%)",
  optimisticInterviewToHirePct: "Optimistic interview-to-hire conversion (%)",
  optimisticConventionalInterviewVolumePct: "Optimistic conventional interview volume (%)",
  optimisticOperationalShortlistTimePct: "Optimistic operational shortlist time (%)",
  optimisticPaidAssessmentReductionPct: "Optimistic paid-assessment reduction (%)",
  additionalExpectedBenefits: "Additional expected benefits",
  risksAndControls: "Risks and controls",
  recommendedPilotScope: "Recommended pilot scope",
};

export const resettableKeys = Object.keys(benchmarkDefaults);
